import { type ThemeMode } from '@/hooks/useDarkMode';
import { cn } from '@/lib/utils';

interface DarkModeToggleProps {
  mode: ThemeMode;
  onModeChange: (mode: ThemeMode) => void;
  className?: string;
}

/**
 * Accessible dark mode toggle button that cycles through light, dark, and system modes.
 * Displays an icon representing the current mode with proper ARIA labels.
 *
 * WCAG AA compliant:
 * - Minimum 4.5:1 contrast ratio for icons
 * - Keyboard accessible (focusable button)
 * - Descriptive aria-label indicating current state and action
 */
export function DarkModeToggle({ mode, onModeChange, className }: DarkModeToggleProps) {
  function handleClick() {
    const next: ThemeMode = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light';
    onModeChange(next);
  }

  const label =
    mode === 'light'
      ? 'Switch to dark mode (currently light)'
      : mode === 'dark'
        ? 'Switch to system theme (currently dark)'
        : 'Switch to light mode (currently system)';

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'relative inline-flex items-center justify-center rounded-lg p-2',
        'text-gray-500 hover:bg-surface-tertiary hover:text-gray-700',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
        'dark:text-gray-400 dark:hover:bg-surface-dark-tertiary dark:hover:text-gray-200',
        'transition-colors',
        className,
      )}
      aria-label={label}
      title={label}
    >
      {mode === 'light' && <SunIcon />}
      {mode === 'dark' && <MoonIcon />}
      {mode === 'system' && <SystemIcon />}
    </button>
  );
}

function SunIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z"
      />
    </svg>
  );
}

function SystemIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z"
      />
    </svg>
  );
}
